import { memo, useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ORGANIZATIONS_LIST_PAGE_SIZE,
  OWNER_SEARCH_DEBOUNCE_MS,
} from '../../../constants/organization';
import './organization-select.css';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
  useOrganizationQuery,
  useOrganizationsQuery,
} from '../../../hooks/useDashboardQueries';
import type { OrganizationV2 } from '../../../types/organization';
import { formatOwnerLabel } from '../../../utils/formatOwnerLabel';

// Legacy documents can lack a name despite the schema, so never assume one.
const organizationLabel = (org: Pick<OrganizationV2, 'id' | 'name' | 'owner'>): string =>
  org.name || (org.owner ? formatOwnerLabel(org.owner) : org.id);

interface OrganizationSelectProps {
  value: string | undefined;
  onChange: (organizationId: string | undefined) => void;
  /** Offer a "no organization" row and a clear button. Default true. */
  allowNone?: boolean;
  disabled?: boolean;
  label?: string;
  /** Shown while nothing is selected and the list is closed. */
  placeholder?: string;
}

/**
 * Organisation autocomplete (station form, System page). Searches server-side (name
 * or owner email/phone) so it is not limited to the first page of
 * organisations; the selected name is resolved by id so it shows even when the
 * organisation is outside the current results.
 */
export const OrganizationSelect = memo(function OrganizationSelect({
  value,
  onChange,
  allowNone = true,
  disabled = false,
  label,
  placeholder,
}: OrganizationSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const debouncedTerm = useDebouncedValue(term, OWNER_SEARCH_DEBOUNCE_MS);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const { data, isFetching } = useOrganizationsQuery(
    1,
    ORGANIZATIONS_LIST_PAGE_SIZE,
    debouncedTerm.trim(),
    open,
  );
  const { data: selected } = useOrganizationQuery(value);

  // Results belong to the debounced term; until it catches up there is
  // nothing to report yet.
  const settled = debouncedTerm === term;
  const options: OrganizationV2[] = data?.data ?? [];
  // With allowNone, index 0 is the "no organization" row; organisations follow.
  const offset = allowNone ? 1 : 0;
  const rowCount = options.length + offset;

  const close = useCallback(() => {
    setOpen(false);
    setTerm('');
  }, []);
  useClickOutside(wrapperRef, close, open);

  const openList = () => {
    if (disabled) return;
    setActiveIndex(0);
    setOpen(true);
  };

  const pick = (organizationId: string | undefined) => {
    onChange(organizationId);
    close();
    inputRef.current?.blur();
  };

  const pickRow = (index: number) => {
    if (allowNone && index === 0) {
      pick(undefined);
      return;
    }
    // Results can shrink under a keyboard highlight; never clear by accident.
    const org = options[index - offset];
    if (org) {
      pick(org.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          openList();
        } else if (rowCount > 0) {
          setActiveIndex((i) => (i + 1) % rowCount);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open && rowCount > 0) {
          setActiveIndex((i) => (i - 1 + rowCount) % rowCount);
        }
        break;
      case 'Enter':
        if (open) {
          e.preventDefault();
          // The list still shows the previous term's results until the search
          // catches up; Enter now would pick a row the user never meant.
          if (settled && !isFetching) {
            pickRow(activeIndex);
          }
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          close();
        }
        break;
      case 'Tab':
        close();
        break;
    }
  };

  const selectedName = value
    ? (selected ? organizationLabel(selected) : t('dashboard.metrics.loading'))
    : '';
  const showNoResults = settled && !isFetching && options.length === 0;
  const optionId = (index: number) => `${listboxId}-opt-${index}`;

  return (
    <div className={`org-combobox${open ? ' is-open' : ''}`} ref={wrapperRef}>
      <div className="org-combobox-field">
        <span className="material-symbols-outlined org-combobox-icon" aria-hidden="true">
          {open ? 'search' : 'corporate_fare'}
        </span>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          className="field-input org-combobox-input"
          autoComplete="off"
          aria-label={label ?? t('dashboard.createStationV2.organizationLabel')}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open ? optionId(activeIndex) : undefined}
          placeholder={
            open
              ? t('dashboard.createStationV2.organizationSearchPlaceholder')
              : (placeholder ?? t('dashboard.createStationV2.organizationNone'))
          }
          disabled={disabled}
          value={open ? term : selectedName}
          onFocus={openList}
          onClick={() => !open && openList()}
          onChange={(e) => {
            setTerm(e.target.value);
            setActiveIndex(e.target.value.trim() ? offset : 0);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <span className="org-combobox-trailing">
          {open && isFetching && <span className="org-combobox-spinner" aria-hidden="true" />}
          {allowNone && value && !open && !disabled && (
            <button
              type="button"
              className="org-combobox-clear"
              aria-label={t('dashboard.createStationV2.organizationClear')}
              onClick={() => onChange(undefined)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
          )}
          <span className="material-symbols-outlined org-combobox-chevron" aria-hidden="true">
            expand_more
          </span>
        </span>
      </div>

      {open && (
        <ul id={listboxId} role="listbox" className="org-combobox-list">
          {allowNone && (
            <li
              id={optionId(0)}
              role="option"
              aria-selected={!value}
              className={`org-combobox-option org-combobox-option-none${activeIndex === 0 ? ' is-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(0)}
              onClick={() => pickRow(0)}
            >
              <span className="org-combobox-name">{t('dashboard.createStationV2.organizationNone')}</span>
              {!value && (
                <span className="material-symbols-outlined org-combobox-check" aria-hidden="true">check</span>
              )}
            </li>
          )}

          {options.map((org, i) => {
            const index = i + offset;
            const label = organizationLabel(org);
            const isSelected = org.id === value;
            return (
              <li
                key={org.id}
                id={optionId(index)}
                role="option"
                aria-selected={isSelected}
                className={`org-combobox-option${activeIndex === index ? ' is-active' : ''}${isSelected ? ' is-selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => pickRow(index)}
              >
                <span className="org-combobox-avatar" aria-hidden="true">
                  {label.slice(0, 2).toUpperCase()}
                </span>
                <span className="org-combobox-text">
                  <span className="org-combobox-name">{label}</span>
                  {org.name && org.owner && (
                    <span className="org-combobox-meta">{formatOwnerLabel(org.owner)}</span>
                  )}
                </span>
                {isSelected && (
                  <span className="material-symbols-outlined org-combobox-check" aria-hidden="true">check</span>
                )}
              </li>
            );
          })}

          {showNoResults && (
            <li className="org-combobox-note" role="presentation">
              {t('dashboard.common.noResults')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
});

export default OrganizationSelect;
